import {
	createRootRoute,
	createRoute,
	createRouter,
	type LinkProps,
	Outlet,
	type RegisteredRouter,
} from "@tanstack/react-router";
import PageWrapper from "./lib/components/atomics/page-wrapper";
import AddPhoneDialog from "./lib/components/complex/add-phone-dialog";
import ErrorComponent from "./lib/components/complex/error-component";
import NotFoundComponent from "./lib/components/complex/not-found-component";
import { IdentityProvider } from "./lib/contexts/identity-context";
import InfoPage from "./routes/authenticated/info";
import InvoiceDetailsPage from "./routes/authenticated/invoice-details";
import InvoicesPage from "./routes/authenticated/invoices";
import MeterReadingPage from "./routes/authenticated/meter-reading";
import OverviewPage from "./routes/authenticated/overview";
import RelocatePage from "./routes/authenticated/relocate";
import ServiceReportsPage from "./routes/authenticated/service-reports";
import UsagePage from "./routes/authenticated/usage";
import Login, { LoginSearchSchema } from "./routes/login";
import RootRoute from "./routes/root";

const rootRoute = createRootRoute({
	component: RootRoute,
});

const indexRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/",
	errorComponent: ErrorComponent,
});

const unauthenticatedLayoutRoute = createRoute({
	getParentRoute: () => rootRoute,
	id: "unauthenticated",
	component: () => (
		<PageWrapper centerContent>
			<Outlet />
		</PageWrapper>
	),
});

export const loginRoute = createRoute({
	getParentRoute: () => unauthenticatedLayoutRoute,
	path: "logginn",
	component: Login,
	validateSearch: LoginSearchSchema,
});

const authenticatedLayoutRoute = createRoute({
	getParentRoute: () => indexRoute,
	id: "authenticatedLayoutRoute",
	component: () => (
		<IdentityProvider>
			<PageWrapper appendCustomerHeader>
				<AddPhoneDialog />
				<Outlet />
			</PageWrapper>
		</IdentityProvider>
	),
});

const overviewRoute = createRoute({
	getParentRoute: () => authenticatedLayoutRoute,
	id: "overviewRoute",
	component: OverviewPage,
});

const usageRoute = createRoute({
	getParentRoute: () => authenticatedLayoutRoute,
	path: "forbruk",
	component: UsagePage,
});

const meterReadingRoute = createRoute({
	getParentRoute: () => authenticatedLayoutRoute,
	path: "avlesning",
	component: MeterReadingPage,
});

const invoicesRoute = createRoute({
	getParentRoute: () => authenticatedLayoutRoute,
	path: "faktura",
	component: InvoicesPage,
});

export const invoiceDetailsRoute = createRoute({
	getParentRoute: () => authenticatedLayoutRoute,
	path: "faktura/$invoiceType/$invoiceId",
	component: InvoiceDetailsPage,
});

const infoRoute = createRoute({
	getParentRoute: () => authenticatedLayoutRoute,
	path: "info",
	component: InfoPage,
});
const relocateRoute = createRoute({
	getParentRoute: () => authenticatedLayoutRoute,
	path: "flytteskjema",
	component: RelocatePage,
});
const serviceReportsRoute = createRoute({
	getParentRoute: () => authenticatedLayoutRoute,
	path: "service-rapporter",
	component: ServiceReportsPage,
});

const routeTree = rootRoute.addChildren([
	indexRoute,
	unauthenticatedLayoutRoute.addChildren([loginRoute]),
	authenticatedLayoutRoute.addChildren([
		overviewRoute,
		usageRoute,
		meterReadingRoute,
		invoicesRoute,
		invoiceDetailsRoute,
		infoRoute,
		relocateRoute,
		serviceReportsRoute,
	]),
]);

export const router = createRouter({
	routeTree,
	defaultNotFoundComponent: () => <NotFoundComponent />,
	defaultErrorComponent: ({ error }) => <ErrorComponent error={error} />,
});

export type Routes = LinkProps<RegisteredRouter>["to"];
export type Params = LinkProps<RegisteredRouter>["params"];

declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router;
	}
}
